/**
 * Admin → Field oversight tab.
 *
 * Two side-by-side surfaces (stacked on mobile):
 *
 *   ┌────────────────┬──────────────────────┐
 *   │  Rep roster    │   Activity feed      │
 *   │  (list of      │   (every visit note  │
 *   │   reps + their │    across all reps,  │
 *   │   counters)    │    newest first)     │
 *   └────────────────┴──────────────────────┘
 *
 * Clicking a rep opens the edit dialog (profile + territories).
 */

import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { endRepView } from '../../api/actAs';
import { SiteLayout } from '../../components/SiteLayout';
import { TerritoryStates } from '../../components/TerritoryStates';
import {
  type AdminActivityRow,
  type AdminRepRow,
  VISIT_CHANNEL_LABELS,
  VISIT_OUTCOME_LABELS,
  VISIT_OUTCOME_TONES,
  fetchAdminActivity,
  fetchAdminReps,
  useAdminRepMomentum,
  type AdminRepMomentum,
} from '../../api/field';
import { AdminFieldRepDialog } from './AdminFieldRepDialog';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function AdminFieldPage() {
  const navigate = useNavigate();
  // Being on the roster means we're not impersonating — clear any
  // lingering act-as so returning here always exits the rep view.
  //
  // This used to clear the rep but keep their cached data, so backing
  // out of a rep view to the roster and then opening your own
  // workspace served their accounts and logs under your name.
  const queryClient = useQueryClient();
  useEffect(() => {
    endRepView(queryClient);
  }, [queryClient]);

  const [editing, setEditing] = useState<AdminRepRow | null>(null);
  // Click a rep card on the left to filter the activity feed.  null
  // = "show all reps".  Click the same rep again (or the Clear pill)
  // to unfilter.
  const [selectedRepId, setSelectedRepId] = useState<string | null>(null);

  const repsQuery = useQuery({
    queryKey: ['admin', 'field', 'reps'],
    queryFn: fetchAdminReps,
    staleTime: 30_000,
  });
  const activityQuery = useQuery({
    queryKey: ['admin', 'field', 'activity'],
    queryFn: () => fetchAdminActivity(100, 0),
    staleTime: 15_000,
  });

  const reps = repsQuery.data?.items ?? [];

  // Fetched once for the whole roster rather than per row: it's a
  // dozen people, and a query per card would stampede the endpoint on
  // every render of the list.
  const momentumQuery = useAdminRepMomentum();
  const momentumByRep = useMemo(() => {
    const map = new Map<string, AdminRepMomentum>();
    for (const row of momentumQuery.data?.items ?? []) map.set(row.user_id, row);
    return map;
  }, [momentumQuery.data]);
  // Stabilize `activity` so downstream useMemos don't re-fire every
  // render — the inline `?? []` fallback creates a fresh empty array
  // each call which would defeat memoization.
  const activity = useMemo(() => activityQuery.data?.items ?? [], [activityQuery.data?.items]);
  const activityTotal = activityQuery.data?.total ?? 0;

  // Filter to a single rep if one is selected.  Resolve the rep name
  // off the reps list (fall back to the activity row's rep_name if
  // the rep isn't in the roster — defensive for stale data).
  const selectedRep = selectedRepId ? reps.find((r) => r.user_id === selectedRepId) : null;
  const visibleActivity = useMemo(
    () => (selectedRepId ? activity.filter((a) => a.rep_id === selectedRepId) : activity),
    [activity, selectedRepId],
  );
  const dayGroups = useMemo(() => groupByDay(visibleActivity), [visibleActivity]);

  return (
    <SiteLayout subtitle="Regional Sales · Oversight" showDesktopHint>
      <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 4, md: 6 } }}>
        {/* Editorial hero — magazine-style header, matches the rep's
            "Your Sales Floor" page so admin + rep views read as
            siblings in the same product family. */}
        <Box sx={{ mb: { xs: 3, md: 3.5 } }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: GOLD,
              mb: 0.75,
            }}
          >
            Admin · Field Activity
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: { xs: 26, md: 34 },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: INK,
            }}
          >
            Regional Sales Oversight
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              maxWidth: 640,
              fontFamily: FONT_INTER,
              fontStyle: 'italic',
              fontSize: { xs: 13.5, md: 14.5 },
              color: 'rgba(15, 10, 7, 0.62)',
              lineHeight: 1.55,
            }}
          >
            Every representative, every territory — visit notes and activity in one place.
          </Typography>
        </Box>
        <Box sx={{ py: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.4fr)' },
              gap: 3,
            }}
          >
            {/* ───── Rep roster ───── */}
            <Box>
              <SectionEyebrow label="Representatives" />
              {repsQuery.isLoading && (
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1.5 }} />
              )}
              {repsQuery.data && reps.length === 0 && (
                <EmptyPanel
                  label="No representatives yet."
                  hint="Grant a user the Regional Sales Representative role from the Users tab to add them here."
                />
              )}
              {repsQuery.data && reps.length > 0 && (
                <Stack spacing={1.25}>
                  {reps.map((rep) => (
                    <RepRow
                      key={rep.user_id}
                      rep={rep}
                      momentum={momentumByRep.get(rep.user_id) ?? null}
                      selected={selectedRepId === rep.user_id}
                      onSelect={() =>
                        setSelectedRepId((prev) => (prev === rep.user_id ? null : rep.user_id))
                      }
                      onEdit={() => setEditing(rep)}
                      onView={() =>
                        navigate(`/admin/regional-sales/reps/${rep.user_id}/view`, {
                          state: { repName: `${rep.first_name} ${rep.last_name}`.trim() },
                        })
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* ───── Activity feed ───── */}
            <Box>
              <SectionEyebrow label="Activity Feed" />
              {activityQuery.isLoading && (
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1.5 }} />
              )}
              {activityQuery.data && activity.length === 0 && (
                <EmptyPanel
                  label="No activity yet."
                  hint="As representatives log visit notes, they will appear here in real time."
                />
              )}
              {activityQuery.data && activity.length > 0 && (
                <>
                  {/* Filter pill — appears only when a rep is selected.
                  Click X (or Show all) to clear back to the full feed. */}
                  {selectedRep && (
                    <Box
                      sx={{
                        mb: 2,
                        px: 1.25,
                        py: 0.85,
                        borderRadius: 1,
                        bgcolor: 'rgba(187, 140, 63, 0.12)',
                        border: '1px solid rgba(187, 140, 63, 0.42)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FONT_INTER,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                          color: GOLD,
                          lineHeight: 1,
                        }}
                      >
                        Filtered by rep
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_INTER,
                          fontSize: 13,
                          fontWeight: 700,
                          color: INK,
                          lineHeight: 1,
                        }}
                      >
                        {selectedRep.first_name} {selectedRep.last_name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: FONT_INTER,
                          fontSize: 11.5,
                          color: 'rgba(15, 10, 7, 0.55)',
                          lineHeight: 1,
                        }}
                      >
                        {visibleActivity.length} of {activity.length} notes
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Chip
                        label="Show all"
                        size="small"
                        onClick={() => setSelectedRepId(null)}
                        onDelete={() => setSelectedRepId(null)}
                        deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontFamily: FONT_INTER,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          bgcolor: '#fff',
                          color: GOLD,
                          border: `1px solid ${GOLD}`,
                          '& .MuiChip-deleteIcon': {
                            color: GOLD,
                          },
                        }}
                      />
                    </Box>
                  )}

                  {visibleActivity.length === 0 && (
                    <EmptyPanel
                      label="No activity from this rep yet."
                      hint="Pick a different rep on the left, or click Show all to see the full feed."
                    />
                  )}

                  {visibleActivity.length > 0 && <ActivityTimeline groups={dayGroups} />}

                  {!selectedRepId && activityTotal > activity.length && (
                    <Typography
                      sx={{
                        mt: 2,
                        fontFamily: FONT_INTER,
                        fontSize: 11.5,
                        color: 'rgba(15, 10, 7, 0.5)',
                        textAlign: 'center',
                      }}
                    >
                      Showing {activity.length} of {activityTotal} notes.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>

          {editing && (
            <AdminFieldRepDialog open userId={editing.user_id} onClose={() => setEditing(null)} />
          )}
        </Box>
      </Container>
    </SiteLayout>
  );
}

function SectionEyebrow({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        fontFamily: FONT_INTER,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: GOLD,
        mb: 1.5,
      }}
    >
      {label}
    </Typography>
  );
}

function EmptyPanel({ label, hint }: { label: string; hint: string }) {
  return (
    <Box
      sx={{
        p: 3,
        textAlign: 'center',
        border: '1px dashed rgba(187, 140, 63, 0.32)',
        borderRadius: 1.5,
        bgcolor: 'rgba(187, 140, 63, 0.04)',
        fontFamily: FONT_INTER,
        color: 'rgba(15, 10, 7, 0.65)',
      }}
    >
      <MapOutlinedIcon sx={{ fontSize: 28, color: GOLD, opacity: 0.6, mb: 0.5 }} />
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontWeight: 700,
          fontSize: 14,
          color: INK,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          color: 'rgba(15, 10, 7, 0.6)',
        }}
      >
        {hint}
      </Typography>
    </Box>
  );
}

function RepRow({
  rep,
  momentum,
  selected,
  onSelect,
  onEdit,
  onView,
}: {
  rep: AdminRepRow;
  momentum: AdminRepMomentum | null;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onView: () => void;
}) {
  const last = rep.last_visit_at
    ? new Date(rep.last_visit_at).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '— never';
  return (
    <Box
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        p: 1.75,
        bgcolor: selected ? 'rgba(187, 140, 63, 0.10)' : 'rgba(253, 251, 245, 0.78)',
        border: selected ? `1.5px solid ${GOLD}` : '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 1.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: selected ? 'rgba(187, 140, 63, 0.14)' : 'rgba(187, 140, 63, 0.05)',
          borderColor: 'rgba(187, 140, 63, 0.42)',
        },
        '&:focus-visible': {
          outline: `2px solid ${GOLD}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: 14,
            color: INK,
          }}
        >
          {rep.first_name} {rep.last_name}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {rep.email}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
          {rep.territory_states.length === 0 ? (
            <Chip
              label="No territory"
              size="small"
              sx={{
                height: 20,
                fontSize: 10.5,
                fontFamily: FONT_INTER,
                fontWeight: 700,
                letterSpacing: '0.06em',
                bgcolor: 'rgba(161, 58, 58, 0.10)',
                color: '#a13a3a',
              }}
            />
          ) : (
            <TerritoryStates states={rep.territory_states} />
          )}
        </Stack>
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {rep.visit_count_30d} note{rep.visit_count_30d === 1 ? '' : 's'} in 30d · last {last}
          {momentum !== null && ` · active ${momentum.active_days}/${momentum.window_days} days`}
        </Typography>
        {/* The one thing worth surfacing here. Ranking reps against
            each other is the least useful thing an admin can do with
            these numbers; noticing that someone has logged nothing for
            eleven days — on holiday, stuck, or quietly not using the
            app — is what's worth a phone call.

            Fourteen days rather than a week: a rep can lose a week to
            a trade show or leave without anything being wrong, and a
            hint that fires that often stops being read. */}
        {momentum !== null &&
          momentum.days_since_last_activity !== null &&
          momentum.days_since_last_activity >= 14 && (
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 600,
                color: '#a86b1e',
              }}
            >
              Nothing logged in {momentum.days_since_last_activity} days
            </Typography>
          )}
      </Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          sx={{
            textTransform: 'none',
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: 12,
            color: GOLD,
            bgcolor: 'rgba(187, 140, 63, 0.10)',
            border: '1px solid rgba(187, 140, 63, 0.35)',
            borderRadius: 999,
            px: 1.25,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.18)', borderColor: GOLD },
          }}
        >
          Act As Rep
        </Button>
        <Tooltip title="Edit profile & territory" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label="Edit representative"
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

/**
 * A visit note that clamps to three lines with a "Show more" toggle.
 *
 * The feed used to render a server-truncated excerpt, so the end of a
 * long note was simply gone — an admin reading "…They were given a
 * barrel, and it is on dis…" had no way to find out what happened
 * next short of opening the account.
 *
 * Clamping in CSS keeps the scan-friendly row height while leaving the
 * full text present and expandable in place. The toggle only renders
 * when the text is actually cut off, measured after layout — guessing
 * from character count is wrong at any width, and would put a dead
 * "Show more" under notes that already fit.
 */
function ExpandableNote({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Re-measure on resize: a note that fits on a wide screen may not
    // on a narrow one.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography
        ref={ref}
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          color: 'rgba(15, 10, 7, 0.7)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
        }}
      >
        “{text}”
      </Typography>
      {(clamped || expanded) && (
        <Box
          component="button"
          type="button"
          onClick={(e: MouseEvent) => {
            // The row is itself clickable; expanding must not navigate.
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          sx={{
            mt: 0.25,
            p: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            fontWeight: 700,
            color: GOLD,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Box>
      )}
    </Box>
  );
}

function ActivityRow({ row }: { row: AdminActivityRow }) {
  const tone = VISIT_OUTCOME_TONES[row.outcome];
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'rgba(253, 251, 245, 0.78)',
        border: '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: 13,
            color: INK,
          }}
        >
          {row.rep_name}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          → {row.account_name}
          {row.account_state ? ` (${row.account_state})` : ''}
        </Typography>
        <Chip
          label={VISIT_OUTCOME_LABELS[row.outcome]}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            letterSpacing: '0.06em',
            bgcolor: `${tone}1a`,
            color: tone,
            border: `1px solid ${tone}55`,
            ml: 'auto',
          }}
        />
      </Box>
      <ExpandableNote text={row.note_text || row.note_excerpt} />
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 11,
          color: 'rgba(15, 10, 7, 0.5)',
        }}
      >
        {VISIT_CHANNEL_LABELS[row.channel]} · {row.visit_date} ·{' '}
        {new Date(row.created_at).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </Typography>
    </Box>
  );
}

// =====================================================================
// Day-grouped activity timeline.
// =====================================================================
interface DayGroup {
  /** ISO YYYY-MM-DD — stable React key + sort anchor. */
  date: string;
  /** Display label: "Today" / "Yesterday" / "Jun 16, 2026". */
  label: string;
  /** Newest-first items within this day (sorted by created_at). */
  items: AdminActivityRow[];
  /** Unique reps that logged activity on this day — surfaces "3 reps
   *  worked Tuesday" energy in the day header. */
  repCount: number;
}

function groupByDay(items: AdminActivityRow[]): DayGroup[] {
  const buckets = new Map<string, AdminActivityRow[]>();
  for (const item of items) {
    const day = item.visit_date;
    const bucket = buckets.get(day);
    if (bucket) bucket.push(item);
    else buckets.set(day, [item]);
  }
  const groups: DayGroup[] = [];
  for (const [date, rows] of buckets.entries()) {
    rows.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
    const reps = new Set(rows.map((r) => r.rep_id));
    groups.push({
      date,
      label: formatDayLabel(date),
      items: rows,
      repCount: reps.size,
    });
  }
  // Newest day at top.
  groups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return groups;
}

function formatDayLabel(isoDay: string): string {
  // Parse the YYYY-MM-DD as a local date to avoid the UTC-shift bug.
  const [y, m, d] = isoDay.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return isoDay;
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDay = new Date(target);
  targetDay.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - targetDay.getTime()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return target.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: target.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
}

function ActivityTimeline({ groups }: { groups: DayGroup[] }) {
  return (
    <Box
      sx={{
        position: 'relative',
        // Vertical gold rail aligned with the day-header dots.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 6,
          top: 6,
          bottom: 6,
          width: 2,
          bgcolor: 'rgba(187, 140, 63, 0.32)',
          borderRadius: 1,
        },
      }}
    >
      <Stack spacing={2.5}>
        {groups.map((group) => (
          <Box key={group.date}>
            {/* Day header — dot on the rail + label + counts.
                Wraps gracefully on narrow widths so the "Today · 3
                notes from 2 reps" string drops to a second line
                rather than overflowing. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 1.25,
                flexWrap: 'wrap',
                rowGap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#fff',
                  border: `2px solid ${GOLD}`,
                  flexShrink: 0,
                  // Slight outer ring for depth.
                  boxShadow: '0 0 0 3px rgba(187, 140, 63, 0.08)',
                }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  lineHeight: 1,
                }}
              >
                {group.label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_INTER,
                  fontSize: 11.5,
                  color: 'rgba(15, 10, 7, 0.55)',
                  lineHeight: 1,
                }}
              >
                · {group.items.length} {group.items.length === 1 ? 'note' : 'notes'}
                {group.repCount > 1 && ` from ${group.repCount} reps`}
              </Typography>
            </Box>

            {/* Items column — left-padded so they sit clear of the
                vertical rail.  Each card stays the existing visual
                treatment; the timeline framing wraps it without
                touching the row chrome. */}
            <Stack spacing={1.25} sx={{ pl: 3.5 }}>
              {group.items.map((row) => (
                <ActivityRow key={row.note_id} row={row} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
